import { useEffect, useState } from "react";
import styled from "styled-components";
import { fmtUsd, fmtCompactUsd, satsToBtc, btcToString } from "./utils";
import { BtcBalPara, UsdBalPara } from "./sharedStyleds";
import { findCuratedWallet } from "./curatedWalletData";
import { netMovedSats } from "./txValue";
import { Link } from "react-router";

const DetailP = styled.p`
  margin: 0;
`;

const Section = styled.div`
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
`;

const SummaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem 0;
  margin-top: 5px;
  margin-bottom: 5px;
`;

const SummaryCol = styled.div`
  min-width: 50%;
`;

const Muted = styled.span`
  color: #a3abba;
`;

// Every Telegram alert links here, so this is the page bot followers land on.
// The addresses were already links, but in the page's text colour with no
// underline, so nothing said they could be followed. Same blue as the address
// links on the sanctions page.
const AddressLink = styled(Link)`
  color: #62c4ff;
  word-break: break-all;
`;

const External = styled.a`
  color: #62c4ff;
`;

// The site already knows some of these addresses - it highlights them on the
// live feed and gives them their own pages - and this view was still printing
// them as raw strings. Same blue as the tag on Bitcoin Live, so a known wallet
// looks the same wherever it turns up.
const WalletTag = styled(Link)`
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0 0.35rem;
  border: 1px solid #32a1ce;
  border-radius: 3px;
  font-size: 0.7rem;
  white-space: nowrap;
  text-decoration: none;

  &:hover {
    background-color: #32a1ce;
    color: #222225;
  }
`;

const DAY = 86400000;

// The BTC price nearest a moment, from the cached price history /api/chart
// already serves: hourly points for the last 30 days, daily for the year.
// Returns null past a year, where there is nothing to look up.
//
// Why bother: every dollar figure on this page used today's price beside a
// "Sent at" date that could be days old, with nothing saying so. A transfer
// posted to Telegram as "$69M" read here as a different number the next day.
// Measured against block 966,708, the nearest hourly point is four minutes
// from the block and gives $69.4M, which is what the alert said.
const priceAt = async (timeMs) => {
  const age = Date.now() - timeMs;
  const range = age <= 30 * DAY ? "1M" : age <= 365 * DAY ? "1Y" : null;
  if (!range) return null;

  const response = await fetch(`/api/chart?symbol=BTC&range=${range}`);
  const points = await response.json().catch(() => null);
  if (!response.ok || !Array.isArray(points) || points.length === 0) return null;

  const [time, price] = points.reduce((best, point) =>
    Math.abs(point[0] - timeMs) < Math.abs(best[0] - timeMs) ? point : best
  );
  return { price, hourly: range === "1M", offByMs: Math.abs(time - timeMs) };
};

// Inputs and outputs render identically; only the field holding the address
// and the amount differs between them.
const AddressRow = ({ address, sats, usd, emptyLabel }) => {
  const btc = satsToBtc(sats);
  const curated = address ? findCuratedWallet(address) : undefined;
  return (
    <DetailP>
      {/* An output with no address is an OP_RETURN or a non-standard script.
          It has no page to link to; linking it made /btc-explorer/undefined. */}
      {address ? (
        <AddressLink to={`/btc-explorer/${address}`}>{address}</AddressLink>
      ) : (
        <Muted>{emptyLabel}</Muted>
      )}{" "}
      {btcToString(btc)} BTC ({usd(btc)})
      {curated && (
        <WalletTag to={`/btc-explorer/${address}`}>{curated.label}</WalletTag>
      )}
    </DetailP>
  );
};

const FullTxDetails = ({ txData, btcPrice }) => {
  const { fee, hash, inputs, out, size, weight, time } = txData;

  // blockchain.info's rawtx carries when it first saw the transaction, not
  // when it was mined, and no confirmation count. mempool.space answers both,
  // with CORS headers, in two small requests.
  // undefined while loading, null when mempool.space could not say, so a
  // failed lookup does not read "Checking confirmations…" forever.
  const [status, setStatus] = useState(undefined);
  const [tipHeight, setTipHeight] = useState(null);
  const [historic, setHistoric] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    setStatus(undefined);
    setTipHeight(null);
    setHistoric(undefined);

    (async () => {
      try {
        const [statusResponse, tipResponse] = await Promise.all([
          fetch(`https://mempool.space/api/tx/${encodeURIComponent(hash)}/status`),
          fetch("https://mempool.space/api/blocks/tip/height"),
        ]);
        const txStatus = statusResponse.ok ? await statusResponse.json() : null;
        const tip = tipResponse.ok ? Number(await tipResponse.text()) : null;
        if (cancelled) return;
        setStatus(txStatus);
        setTipHeight(Number.isFinite(tip) ? tip : null);

        // Priced at the block when there is one, at first sight otherwise.
        const when =
          txStatus?.confirmed && txStatus.block_time
            ? txStatus.block_time * 1000
            : time * 1000;
        // Within the hour, today's price is the price at the time.
        const found = Date.now() - when < 3600000 ? null : await priceAt(when);
        if (!cancelled) setHistoric(found);
      } catch (error) {
        // Losing the block and the historic price costs detail, not the page:
        // everything below still renders from rawtx and the spot price.
        console.error("Transaction status lookup failed:", error);
        if (!cancelled) {
          setStatus((current) => (current === undefined ? null : current));
          setHistoric(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hash, time]);

  // Held back until the price is settled. Rendering at today's price first and
  // then swapping to the historic one would flash exactly the number this
  // exists to correct.
  const usdPrice = historic === undefined ? null : historic?.price ?? btcPrice;
  const usd = (btc) => (usdPrice === null ? "…" : `$${fmtUsd(btc * usdPrice)}`);
  const priceNote =
    historic === undefined
      ? null
      : historic
      ? `at $${fmtUsd(historic.price)}/BTC, the ${
          historic.hourly ? "hourly" : "daily"
        } price when ${status?.confirmed ? "it was mined" : "it was first seen"}`
      : `at today's price of $${fmtUsd(btcPrice)}/BTC`;

  // A coinbase - the first transaction in every block, where the miner is paid -
  // has one input that spends nothing: no address, no value. It is the
  // transaction every block explorer link from a mining pool page lands on, and
  // it read as a payment from "no address" with a fee of 0.0 sat/vB.
  const isCoinbase =
    inputs.length === 1 &&
    !inputs[0].prev_out?.addr &&
    !inputs[0].prev_out?.value;

  const inputTotalBtc = satsToBtc(
    inputs.reduce((prev, cur) => prev + (cur.prev_out?.value || 0), 0)
  );
  const outputTotalBtc = satsToBtc(
    out.reduce((prev, cur) => prev + cur.value, 0)
  );

  // The headline is what moved, the same figure the whale alerts post, rather
  // than the output total, which counts change returning to the sender. When
  // the two differ the output total is shown beside it, so neither hides the
  // other.
  const movedBtc = satsToBtc(netMovedSats(txData));
  const movedDiffers = Math.abs(movedBtc - outputTotalBtc) >= 0.00000001;

  const feeInBtc = satsToBtc(fee);
  const vbytes = weight ? Math.ceil(weight / 4) : null;
  const feeRate = vbytes ? fee / vbytes : null;

  const confirmations =
    status?.confirmed && tipHeight ? tipHeight - status.block_height + 1 : null;

  const fmtTime = (seconds) => new Date(seconds * 1000).toLocaleString();

  return (
    <>
      <SummaryRow>
        <SummaryCol>
          <Muted>{isCoinbase ? "Newly issued to the miner" : "Moved"}</Muted>
          <BtcBalPara>{btcToString(movedBtc)} BTC</BtcBalPara>
          <UsdBalPara>
            ≈ {usdPrice === null ? "…" : fmtCompactUsd(movedBtc * usdPrice)}
          </UsdBalPara>
          {priceNote && (
            <DetailP>
              <Muted>{priceNote}</Muted>
            </DetailP>
          )}
          {movedDiffers && (
            <DetailP>
              <Muted>
                of {btcToString(outputTotalBtc)} BTC in outputs; the rest reads
                as change, not a payment
              </Muted>
            </DetailP>
          )}
        </SummaryCol>
        <SummaryCol>
          <DetailP>
            {status === undefined ? (
              <Muted>Checking confirmations…</Muted>
            ) : status === null ? (
              <Muted>Confirmation status unavailable</Muted>
            ) : status.confirmed ? (
              <>
                Confirmed in block {status.block_height.toLocaleString()}
                {confirmations !== null &&
                  ` · ${confirmations.toLocaleString()} confirmation${
                    confirmations === 1 ? "" : "s"
                  }`}
              </>
            ) : (
              "Unconfirmed - waiting in the mempool"
            )}
          </DetailP>
          {status?.confirmed && status.block_time && (
            <DetailP>Mined: {fmtTime(status.block_time)}</DetailP>
          )}
          <DetailP>First seen: {fmtTime(time)}</DetailP>
          <DetailP>
            {isCoinbase ? (
              <>
                Fee: <Muted>none - this pays the miner the block reward and fees</Muted>
              </>
            ) : (
              <>
                Fee: {btcToString(feeInBtc)} BTC ({usd(feeInBtc)})
                {feeRate !== null && ` · ${feeRate.toFixed(1)} sat/vB`}
              </>
            )}
          </DetailP>
          <DetailP>
            Size: {size} bytes{vbytes !== null && ` · ${vbytes} vB`}
          </DetailP>
        </SummaryCol>
      </SummaryRow>
      <DetailP style={{ wordBreak: "break-all" }}>Hash: {hash}</DetailP>
      <DetailP>
        <External
          href={`https://mempool.space/tx/${hash}`}
          target="_blank"
          rel="noreferrer noopener"
        >
          Check this transaction on mempool.space
        </External>
      </DetailP>
      <Section>
        <strong>
          Inputs - {btcToString(inputTotalBtc)} BTC ({usd(inputTotalBtc)}):
        </strong>
        {/* keyed by position, not address: one address funding a transaction
            twice is ordinary, and keying by address dropped the duplicate */}
        {inputs.map((x, i) => (
          <AddressRow
            key={i}
            address={x.prev_out?.addr}
            sats={x.prev_out?.value || 0}
            usd={usd}
            emptyLabel={
              isCoinbase
                ? "Coinbase - new coins created with the block, no sender"
                : "No address (non-standard input)"
            }
          />
        ))}
      </Section>
      <Section>
        <strong>
          Outputs - {btcToString(outputTotalBtc)} BTC ({usd(outputTotalBtc)}):
        </strong>
        {out.map((x, i) => (
          <AddressRow
            key={i}
            address={x.addr}
            sats={x.value}
            usd={usd}
            emptyLabel="No address (data or non-standard output)"
          />
        ))}
      </Section>
    </>
  );
};

export default FullTxDetails;
